import CheckIcon from "@mui/icons-material/Check";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import InsightsIcon from "@mui/icons-material/QueryStats";
import {
  Box,
  Button,
  LinearProgress,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { t } from "i18next";
import { Link as RouterLink } from "react-router-dom";
import { useTheme } from "../../context/ThemeContext";
import {
  Prerequisite,
  PREREQUISITE_ROUTES,
} from "../../shared/casePrerequisites";
import { EmptyStateFrame, EmptyStateHeader } from "../general/EmptyState";

// a met row gets a faint wash of the positive colour — a nod, not a highlight
const MET_ROW_TINT_ALPHA = 0.06;

/**
 * What the case still needs before its analytics mean anything.
 *
 * Shown instead of the numbers rather than beside them: the engine substitutes
 * defaults for missing input and produces a confident answer either way, so
 * rendering both would present an assumption as a result. Each row names what is
 * missing and links to where it is entered.
 */
export const PrerequisiteChecklist = ({
  caseId,
  prerequisites,
}: {
  caseId: string;
  prerequisites: Prerequisite[];
}) => {
  const { theme } = useTheme();

  const done = prerequisites.filter(({ met }) => met).length;
  const remaining = prerequisites.length - done;
  // the call to action must land somewhere that can actually be acted on
  const firstActionable = prerequisites.find(
    ({ met, blocked }) => !met && !blocked,
  );

  const routeTo = (p: Prerequisite) =>
    `/cases/${caseId}/${PREREQUISITE_ROUTES[p.id]}`;

  /** The met row shows what was found; the unmet row shows what is wanted. */
  const secondary = (p: Prerequisite) => {
    if (p.met) return p.detail;
    if (p.id === "winChances" && p.missing)
      return t("case.prerequisites.winChances.missing", { count: p.missing });
    return t(`case.prerequisites.${p.id}.hint`);
  };

  return (
    <EmptyStateFrame>
      <EmptyStateHeader
        icon={<InsightsIcon />}
        title={t("case.prerequisites.title", { count: remaining })}
        intro={t("case.prerequisites.intro")}
      />

      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          gap: theme.spacing(2),
          width: "100%",
        }}
      >
        <LinearProgress
          variant="determinate"
          value={
            prerequisites.length > 0 ? (done / prerequisites.length) * 100 : 0
          }
          color="success"
          sx={{ flex: 1, height: theme.spacing(1), borderRadius: 1 }}
        />
        <Typography
          variant="caption"
          noWrap
          sx={{
            color: "text.secondary",
          }}
        >
          {t("case.prerequisites.progress", {
            done,
            total: prerequisites.length,
          })}
        </Typography>
      </Stack>

      <Stack
        sx={{
          width: "100%",
          bgcolor: "background.paper",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        {prerequisites.map((p) => (
          <Stack
            key={p.id}
            direction="row"
            sx={{
              alignItems: "center",
              gap: theme.spacing(1.5),
              px: theme.spacing(2),
              py: theme.spacing(1.5),
              borderTop: `1px solid ${theme.palette.divider}`,
              "&:first-of-type": { borderTop: "none" },
              bgcolor: p.met
                ? alpha(theme.palette.data.positive.main, MET_ROW_TINT_ALPHA)
                : undefined,
              opacity: p.blocked ? 0.55 : 1,
            }}
          >
            {p.met ? (
              <CheckIcon
                fontSize="small"
                sx={{ color: theme.palette.data.positive.dark }}
              />
            ) : (
              <RadioButtonUncheckedIcon
                fontSize="small"
                sx={{ color: theme.palette.text.disabled }}
              />
            )}

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  color: p.blocked
                    ? "text.disabled"
                    : p.met
                      ? "text.secondary"
                      : "text.primary",
                  fontWeight: "bold",
                }}
              >
                {t(`case.prerequisites.${p.id}.label`)}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "text.disabled",
                }}
              >
                {secondary(p)}
              </Typography>
            </Box>

            {/* no link while blocked — it would only bounce off whatever this
                step depends on and land the reader back where they started */}
            {!p.met && !p.blocked && (
              <Link component={RouterLink} to={routeTo(p)} variant="body2">
                {t(`case.prerequisites.${p.id}.action`)}
              </Link>
            )}
          </Stack>
        ))}
      </Stack>

      {firstActionable && (
        <Button
          variant="contained"
          component={RouterLink}
          to={routeTo(firstActionable)}
          endIcon={<ChevronRightIcon />}
        >
          {t("case.prerequisites.cta", {
            name: t(`case.prerequisites.${firstActionable.id}.name`),
          })}
        </Button>
      )}
    </EmptyStateFrame>
  );
};
